import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Box, Button, Grid, Link, Switch, Typography } from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import { green, grey } from '@material-ui/core/colors';
import PhoneNumberInput from 'material-ui-phone-number';
import { Formik, Field, Form } from 'formik';
import { injectIntl } from 'react-intl';

import messages from './messages';
import Panel from '../../../common/elements/Panel';
import useGlobalContext from '../../../common/hooks/useGlobalContext';
import {
  validateEmail,
  validatePhoneNumber,
  validateRequired,
} from '../../../common/validation/validation';
import capitalizeFirstChar from '../../../common/utils/capitalizeFirstChar';
import generateRandomPassword from '../../../common/generateRandomPassword';
import FormikTextFields from '../../../common/elements/FormikTextField';

const ADMIN = 'admin';
const RIDER = 'rider';
const USER = 'user';
const carsharingUserUrl = `/api/v3/user_invites/`;

const ProfileFormPanel = (props) => {
  const {
    fetchAdmins,
    fetchRiders,
    fetchUsers,
    intl: { formatMessage },
    isCarsharing,
    setLoader,
    setRightSidePanelOpen,
  } = props;
  const {
    state: {
      activeBusinessId,
      profileFormPanel: { type, data },
    },
    setError,
    setLoading,
    setToastState,
  } = useGlobalContext();
  const [accountStatusChecked, setAccountStatusChecked] = useState(null);

  useEffect(() => {
    setAccountStatusChecked(null);
  }, [data]);

  const handleAccountStatusChange = (event) => {
    setAccountStatusChecked(event.target.checked);
  };

  const handleResendCode = async (id, event) => {
    event.preventDefault();
    setLoader(true);
    try {
      await axios.post(`${carsharingUserUrl}${id}/resend_invitation`);
      setLoader(false);
      setToastState({
        message: formatMessage(messages.resendCodeSent),
        open: true,
        variant: 'success',
      });
    } catch (error) {
      setLoader(false);
      if (error.response.data?.message)
        setToastState({ message: error.response.data.message, open: true, variant: 'error' });
      else setError(error);
    }
  };

  const renderTitle = () => {
    let title = formatMessage(messages.addAdmin);
    if (type === ADMIN && data) title = formatMessage(messages.editAdmin);
    else if (type === RIDER && data) title = formatMessage(messages.editRider);
    else if (type === RIDER && !data) title = formatMessage(messages.addRider);
    else if (type === USER && data) title = formatMessage(messages.editUser);
    else if (type === USER && !data) title = formatMessage(messages.addUser);
    return title;
  };

  const validateForm = (values) => {
    let errors = {};
    const validationResults = {
      first_name: validateRequired(values.first_name),
      last_name: validateRequired(values.last_name),
      email: validateRequired(values.email)
        ? validateRequired(values.email)
        : validateEmail(values.email),
      password: type === ADMIN && !data && validateRequired(values.password),
    };
    if (Object.values(validationResults).some((value) => Boolean(value))) {
      errors = validationResults;
    }
    return errors;
  };

  const shouldUpdateAccountStatus = (prevStatus, isChecked) => {
    if (
      (prevStatus === 'ACCEPTED' && isChecked) ||
      (prevStatus === 'INVITED' && isChecked) ||
      (prevStatus === 'REVOKED' && !isChecked)
    ) {
      return null;
    }
    return isChecked ? { status: 'INVITED' } : { status: 'REVOKED' };
  };

  const shapeInitialValues = () => ({
    first_name: data ? data.first_name : '',
    last_name: data ? data.last_name : '',
    email: data ? data.email : '',
    rfid: data?.rfid ? data.rfid : '',
    ...(type === ADMIN && { password: '' }),
    ...(data && data.status === 'INVITED' && { accepted: true, invited: true }),
    ...(data && data.status === 'ACCEPTED' && { accepted: true }),
    ...(data && data.status === 'REVOKED' && { accepted: false }),
  });
  const sendFormValues = (
    { first_name, last_name, email, password, rfid, ...rest },
    { resetForm, setSubmitting }
  ) => {
    const isAdmin = type === ADMIN;
    const url = `/api/v2/${isAdmin ? 'staff/' : 'customers/'}`;

    let values;
    if (isCarsharing) {
      const isEditing = data;
      if (isEditing) {
        const updatedAccountStatus = shouldUpdateAccountStatus(data.status, accountStatusChecked);
        values = {
          first_name,
          last_name,
          rfid,
          ...updatedAccountStatus,
        };
      } else {
        values = {
          business_entity_id: activeBusinessId,
          email,
          first_name,
          last_name,
          rfid,
        };
      }
    } else if (isAdmin) {
      values = {
        business_admin: true,
        business_entity: [{ id: activeBusinessId, is_business_admin: true }],
        manager: false,
        user: {
          first_name,
          last_name,
          rfid,
          username: email,
          ...(!data && { email, password }),
        },
        ...(data && { user_profile: data.user_profile }),
      };
    } else {
      values = {
        ...(!data && {
          business_entity_ids: [activeBusinessId],
          notification_type: 'sms',
          email,
          password: generateRandomPassword(),
        }),
        first_name,
        last_name,
        rfid,
      };
    }

    const request = isCarsharing
      ? () =>
          data
            ? axios.patch(`${carsharingUserUrl}${data.id}`, values)
            : axios.post(carsharingUserUrl, values)
      : () => (data ? axios.patch(`${url}${data.id}`, values) : axios.post(url, values));

    const cleanUp = () => {
      setLoading(true);
      if (isAdmin) {
        fetchAdmins().finally(() => setLoading(false));
      }
      if (isCarsharing) {
        setRightSidePanelOpen(false);
        fetchUsers().finally(() => {
          setLoading(false);
        });
      } else {
        fetchRiders().finally(() => setLoading(false));
      }
      if (!data) {
        resetForm(shapeInitialValues());
      }
      setSubmitting(false);
    };

    return request()
      .then(() => cleanUp())
      .catch((e) => {
        setError(e);
        setSubmitting(false);
        setLoading(false);
      });
  };

  const AccountSwitch = withStyles({
    switchBase: {
      color: grey[100],
      '&$checked': {
        color: grey[100],
      },
      '&$checked + $track': {
        backgroundColor: green[500],
      },
    },
    checked: {},
    track: {},
  })(Switch);

  return (
    <Panel title={renderTitle()}>
      <Formik
        enableReinitialize
        onSubmit={sendFormValues}
        initialValues={shapeInitialValues()}
        validateOnBlur={false}
        validateOnChange={false}
        validate={validateForm}
      >
        {(formikProps) => {
          const { dirty, errors, initialValues, isSubmitting, setFieldValue } = formikProps;

          return (
            <Form>
              <Box>
                <FormikTextFields
                  formikProps={formikProps}
                  label={formatMessage(messages.firstName)}
                  name="first_name"
                  required
                />
              </Box>
              <Box mt={2}>
                <FormikTextFields
                  formikProps={formikProps}
                  label={formatMessage(messages.lastName)}
                  name="last_name"
                  required
                />
              </Box>
              {(isCarsharing || !data) && (
                <Box mt={2}>
                  <FormikTextFields
                    disabled={isCarsharing && data}
                    formikProps={formikProps}
                    label={formatMessage(messages.email)}
                    name="email"
                    required
                  />
                </Box>
              )}
              {type === ADMIN && !data && (
                <Box mt={2}>
                  <FormikTextFields
                    formikProps={formikProps}
                    isPassword
                    label={formatMessage(messages.password)}
                    name="password"
                    required
                  />
                </Box>
              )}
              <Box mt={2}>
                <FormikTextFields
                  formikProps={formikProps}
                  label={formatMessage(messages.rfid)}
                  name="rfid"
                  disabled={data?.customer_id}
                />
              </Box>
              {type === ADMIN && !data && (
                <Box mt={2}>
                  <FormikTextFields
                    formikProps={formikProps}
                    isPassword
                    label={formatMessage(messages.password)}
                    name="password"
                    required
                  />
                </Box>
              )}
              {type === USER && data && (
                <Box display="flex" justifyContent="space-between" mt={2}>
                  <>
                    <Typography color={'textSecondary'} component="div">
                      {formatMessage(messages.accountStatus, {
                        status: data.status
                          ? capitalizeFirstChar(data.status)
                          : formatMessage(messages.na),
                      })}
                    </Typography>
                    <Field as="label" name="accepted">
                      <Typography component="div">
                        <Grid
                          component="label"
                          container
                          alignItems="center"
                          direction="column"
                          spacing={1}
                        >
                          <Grid container alignItems="center" direction="row">
                            <Grid item>
                              {accountStatusChecked
                                ? formatMessage(messages.activeStatus)
                                : formatMessage(messages.inactiveStatus)}
                            </Grid>
                            <Grid item>
                              <AccountSwitch
                                checked={
                                  accountStatusChecked !== null
                                    ? accountStatusChecked
                                    : setAccountStatusChecked(initialValues.accepted)
                                }
                                onChange={(e) => {
                                  setFieldValue('status', e);
                                  handleAccountStatusChange(e);
                                }}
                                name="accountStatusChecked"
                              />
                            </Grid>
                          </Grid>
                        </Grid>
                        <Grid item>
                          {initialValues.invited && (
                            <Link
                              component="button"
                              variant="body2"
                              onClick={(event) => {
                                handleResendCode(data.id, event);
                              }}
                            >
                              {formatMessage(messages.resendCode)}
                            </Link>
                          )}
                        </Grid>
                      </Typography>
                    </Field>
                  </>
                </Box>
              )}
              <Box color="primary" mt={3}>
                <Button
                  color="primary"
                  disabled={!dirty || isSubmitting}
                  fullWidth
                  type="submit"
                  variant="contained"
                >
                  {data ? formatMessage(messages.save) : formatMessage(messages.add)}
                </Button>
              </Box>
            </Form>
          );
        }}
      </Formik>
    </Panel>
  );
};

export default injectIntl(ProfileFormPanel);
